import { Shot } from '@/features/shotlist/store/useShotListStore'
import { Button } from '@/shared/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/shared/components/ui/select'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { Info } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { StoryboardFrame } from '../types/storyboard.types'
import ReferenceImagePicker from './ReferenceImagePicker'

type AddStoryboardFrameDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentSceneShots: Shot[]
    selectedShot: Shot | null
    onAddFrame: (
        frame: Omit<
            StoryboardFrame,
            'id' | 'projectId' | 'sceneId' | 'frameNumber' | 'createdAt' | 'updatedAt'
        >
    ) => void
}

type ManualFrameDraft = Omit<
    StoryboardFrame,
    'id' | 'projectId' | 'sceneId' | 'frameNumber' | 'createdAt' | 'updatedAt'
>

const storyboardFieldHelpers = {
    shotAction: 'Describe the action or story beat shown in this frame.',
    visualDescription:
        'Add visual direction that improves the generated image, like mood, lighting, composition, styling, or atmosphere.',
    imagePromptOverride:
        'Optional override. If provided, the backend uses this prompt instead of generating one from the frame details.',
    dialogueNotes: 'Optional dialogue or performance context tied to this frame.',
}

const DEFAULT_REFERENCE_IMAGE_USAGE = 'General visual guidance'

const createDraftFromShot = (shot: Shot | null): ManualFrameDraft => ({
    shotId: shot?.id ?? '',
    shotDescription: shot?.description ?? '',
    visualDescription: '',
    imagePromptOverride: '',
    dialogueNotes: '',
    imageUrl: '',
    referenceImageUrl: '',
    referenceImageUsage: '',
})

export default function AddStoryboardFrameDialog({
    open,
    onOpenChange,
    currentSceneShots,
    selectedShot,
    onAddFrame,
}: AddStoryboardFrameDialogProps) {
    const defaultShot = useMemo(() => {
        if (selectedShot) return selectedShot
        return currentSceneShots[0] ?? null
    }, [currentSceneShots, selectedShot])

    const [draft, setDraft] = useState<ManualFrameDraft>(createDraftFromShot(defaultShot))
    const activeShot = useMemo(
        () => currentSceneShots.find((item) => item.id === draft.shotId) ?? null,
        [currentSceneShots, draft.shotId]
    )

    useEffect(() => {
        if (open) {
            setDraft(createDraftFromShot(defaultShot))
        }
    }, [defaultShot, open])

    const handleShotChange = (shotId: string) => {
        const shot = currentSceneShots.find((item) => item.id === shotId) ?? null
        setDraft((prev) => ({
            ...prev,
            shotId,
            shotDescription: prev.shotDescription || shot?.description || '',
        }))
    }

    const handleSubmit = () => {
        onAddFrame(draft)
        onOpenChange(false)
    }

    const canSubmit = draft.shotId.trim().length > 0

    const renderFieldWithTooltip = (
        label: string,
        helperText: string,
        children: React.ReactNode
    ) => (
        <div className="space-y-1">
            <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {label}
                </label>
                <TooltipProvider>
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Info className="h-3 w-3 cursor-help text-muted-foreground/50 transition-colors hover:text-primary" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs">
                            {helperText}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {children}
        </div>
    )

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden p-0 sm:max-w-2xl">
                <DialogHeader className="shrink-0 px-6 pt-6">
                    <DialogTitle>Add Frame</DialogTitle>
                    <DialogDescription>
                        Create a storyboard frame manually. New frames keep their creation order by
                        default, and you can rearrange them later.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid flex-1 gap-4 overflow-y-auto px-6 py-2">
                    <div className="grid gap-2">
                        <label className="text-sm font-medium">Shot</label>
                        <Select value={draft.shotId} onValueChange={handleShotChange}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a shot" />
                            </SelectTrigger>
                            <SelectContent>
                                {currentSceneShots.map((shot, index) => (
                                    <SelectItem key={shot.id} value={shot.id}>
                                        Shot {index + 1}: {shot.subject || shot.description || shot.shotType}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {activeShot && (
                        <div className="rounded-lg border bg-muted/20 p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                Shot Details
                            </p>
                            <div className="mt-3 grid gap-3 sm:grid-cols-4">
                                <div>
                                    <p className="text-xs text-muted-foreground">Camera Angle</p>
                                    <p className="text-sm font-medium">
                                        {activeShot.cameraAngle || 'Not set'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Camera Movement</p>
                                    <p className="text-sm font-medium">
                                        {activeShot.cameraMovement || 'Not set'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Duration</p>
                                    <p className="text-sm font-medium">
                                        {activeShot.duration || 'Not set'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Framing</p>
                                    <p className="text-sm font-medium">
                                        {activeShot.framing || 'Not set'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {renderFieldWithTooltip(
                        'Shot Action',
                        storyboardFieldHelpers.shotAction,
                        <Textarea
                            value={draft.shotDescription}
                            onChange={(e) =>
                                setDraft((prev) => ({ ...prev, shotDescription: e.target.value }))
                            }
                            placeholder="Describe the action in this frame..."
                            className="min-h-[90px]"
                        />
                    )}

                    {renderFieldWithTooltip(
                        'Visual Description',
                        storyboardFieldHelpers.visualDescription,
                        <Textarea
                            value={draft.visualDescription}
                            onChange={(e) =>
                                setDraft((prev) => ({ ...prev, visualDescription: e.target.value }))
                            }
                            placeholder="Describe the composition, mood, and visual details..."
                            className="min-h-[110px]"
                        />
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            {renderFieldWithTooltip(
                                'Image Prompt Override',
                                storyboardFieldHelpers.imagePromptOverride,
                            <Input
                                value={draft.imagePromptOverride || ''}
                                onChange={(e) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        imagePromptOverride: e.target.value,
                                    }))
                                }
                                placeholder="Optional AI prompt override"
                            />
                            )}
                        </div>
                    </div>

                    {renderFieldWithTooltip(
                        'Dialogue Notes',
                        storyboardFieldHelpers.dialogueNotes,
                        <Textarea
                            value={draft.dialogueNotes || ''}
                            onChange={(e) =>
                                setDraft((prev) => ({ ...prev, dialogueNotes: e.target.value }))
                            }
                            placeholder="Optional dialogue or performance notes..."
                            className="min-h-[80px]"
                        />
                    )}

                    <ReferenceImagePicker
                        value={draft.referenceImageUrl || undefined}
                        usage={draft.referenceImageUsage || DEFAULT_REFERENCE_IMAGE_USAGE}
                        onChange={(url) =>
                            setDraft((prev) => ({
                                ...prev,
                                referenceImageUrl: url ?? '',
                                referenceImageUsage: url
                                    ? prev.referenceImageUsage || DEFAULT_REFERENCE_IMAGE_USAGE
                                    : '',
                            }))
                        }
                        onUsageChange={(usage) =>
                            setDraft((prev) => ({ ...prev, referenceImageUsage: usage }))
                        }
                    />
                </div>

                <DialogFooter className="shrink-0 border-t px-6 py-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        Add Frame
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
